import { A } from "@solidjs/router";
import { For, Show } from "solid-js";
import { BRIEF_DETAIL_KIND } from "../../enums/brief";
import type { DeliveryBrief } from "./briefTypes";

export function BriefSourceContext(props: { brief: DeliveryBrief }) {
  const readableEvidence = () =>
    props.brief.source.evidence.filter(
      (item) => item.kind === BRIEF_DETAIL_KIND.READABLE,
    );
  const technicalEvidence = () =>
    props.brief.source.evidence.filter(
      (item) => item.kind === BRIEF_DETAIL_KIND.TECHNICAL,
    );

  return (
    <>
      <section class="brief-source-section">
        <span class="signal-mini-kicker">Source and provenance</span>
        <h3>{props.brief.source.label}</h3>
        <p>
          Recorded findings and evidence are source material. Delivery
          instructions are proposals for review. Owners, deadlines and missing
          client context need human input.
        </p>
        <A class="brief-source-title" href={props.brief.source.route}>
          {props.brief.source.title}
        </A>
        <Show when={props.brief.source.dimension}>
          {(dimension) => (
            <p>
              <strong>Assessment context</strong>
              {dimension()}
            </p>
          )}
        </Show>
        <Show when={props.brief.source.coverageNote}>
          {(note) => <p class="brief-coverage-note">{note()}</p>}
        </Show>
      </section>

      <section class="brief-source-section">
        <h3>Affected scope</h3>
        <Show
          when={props.brief.pages.length > 0}
          fallback={<p>{props.brief.scopeNotes}</p>}
        >
          <ul class="brief-source-pages">
            <For each={props.brief.pages}>
              {(page) => (
                <li>
                  <Show
                    when={page.detailRoute}
                    fallback={<strong>{page.title}</strong>}
                  >
                    {(route) => <A href={route()}>{page.title}</A>}
                  </Show>
                  <small>
                    {page.path} / Content Fitness{" "}
                    {page.contentFitnessScore ?? "--"}
                  </small>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </section>

      <section class="brief-source-section">
        <h3>Relevant evidence</h3>
        <Show
          when={readableEvidence().length > 0}
          fallback={<p>No readable evidence is stored for this source.</p>}
        >
          <ul class="brief-source-evidence">
            <For each={readableEvidence().slice(0, 3)}>
              {(evidence) => (
                <li>
                  <strong>{evidence.label}</strong>
                  <p>{evidence.detail}</p>
                  <Show when={evidence.route}>
                    {(route) => <A href={route()}>Open linked issue</A>}
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
        <Show when={readableEvidence().length > 3}>
          <details>
            <summary>
              Additional evidence ({readableEvidence().length - 3})
            </summary>
            <ul class="brief-source-evidence">
              <For each={readableEvidence().slice(3)}>
                {(evidence) => (
                  <li>
                    <strong>{evidence.label}</strong>
                    <p>{evidence.detail}</p>
                  </li>
                )}
              </For>
            </ul>
          </details>
        </Show>
      </section>

      <section class="brief-source-section">
        <h3>Brand guidance</h3>
        <Show
          when={props.brief.source.guidance.length > 0}
          fallback={
            <p>No applicable Brand Guidelines are linked to this source.</p>
          }
        >
          <ul>
            <For each={props.brief.source.guidance}>
              {(item) => <li>{item}</li>}
            </For>
          </ul>
        </Show>
      </section>

      <details class="brief-source-technical">
        <summary>Technical detail</summary>
        <Show when={technicalEvidence().length > 0}>
          <ul>
            <For each={technicalEvidence()}>
              {(item) => (
                <li>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </li>
              )}
            </For>
          </ul>
        </Show>
        <dl>
          <For each={props.brief.source.technical}>
            {(item) => (
              <div>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            )}
          </For>
        </dl>
        <nav aria-label="Source routes">
          <For each={props.brief.source.routes}>
            {(route) => <A href={route.href}>{route.label}</A>}
          </For>
        </nav>
      </details>
    </>
  );
}
